import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, COLD_START_STATUSES } from "./api";

const AuthContext = createContext({ user: undefined, login: async () => {}, logout: () => {} });

const TOKEN_KEY = "reperti-token";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined); // undefined = checking, null = anonymous, object = logged in

  // Configure axios interceptor for auth header + 401 handler
  useEffect(() => {
    const reqId = api.interceptors.request.use((config) => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (t) config.headers.Authorization = `Bearer ${t}`;
      return config;
    });
    const resId = api.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401) {
          // Token invalid/expired - clear and redirect (skip on /login or public routes)
          const path = window.location.pathname;
          if (!path.startsWith("/login") && !path.startsWith("/share/")) {
            localStorage.removeItem(TOKEN_KEY);
            if (path !== "/login") window.location.assign("/login");
          }
        }
        return Promise.reject(err);
      },
    );
    return () => {
      api.interceptors.request.eject(reqId);
      api.interceptors.response.eject(resId);
    };
  }, []);

  // Bootstrap: check existing session
  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setUser(null); return; }
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
      });
  }, []);

  const login = useCallback(async (email, password, onRetry) => {
    const { data } = await api.post(
      "/auth/login",
      { email, password },
      { __retryAllowed: true, __onRetry: onRetry },
    );
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    window.location.assign("/login");
  }, []);

  // Re-fetch the current user from the server. Used after operations that
  // change user-state on the backend (e.g. password change clears the
  // `must_change_password` flag).
  const refreshUser = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setUser(null); return null; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      console.warn("auth: refreshUser failed", e);
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export const formatApiError = (err) => {
  const status = err?.response?.status;
  if (err?.__coldStart || !err?.response || COLD_START_STATUSES.includes(status)) {
    return "Il server si sta riavviando dopo un periodo di inattività. Attendi qualche secondo e riprova: al prossimo tentativo funzionerà.";
  }
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Errore di rete";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  }
  return String(detail);
};
